import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import type { Job, JobListParams } from '@/api/types';
import { useSearchParams } from 'react-router-dom';
import JobDetailDrawer from '@/components/jobs/JobDetailDrawer';
import ConfirmRemoveJobModal from '@/components/jobs/ConfirmRemoveJobModal';
import CleanupJobsConfirmModal, { type JobCleanupMode } from '@/components/jobs/CleanupJobsConfirmModal';
import ClearJobActivityConfirmModal from '@/components/jobs/ClearJobActivityConfirmModal';
import BulkDeleteJobsConfirmModal from '@/components/jobs/BulkDeleteJobsConfirmModal';
import { AdminPageSections, BulkActionBar, BulkActionButton, AutoRefreshToolbar, CollapsiblePanel } from '@/components/common';
import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import { DataTable, TruncatedCell, TableActionBar, TABLE_ACTION_MIN_WIDTH, tableActionsColumnClass, type DataTableColumn } from '@/components/table';
import {
  EmptyState,
  ErrorBanner,
  FilterSelect,
  MetricChip,
  Toolbar,
  ExportButtons,
} from '@/components/ui';
import StatusBadge from '@/components/ui/StatusBadge';
import { useToast } from '@/components/Toast';
import { useMe } from '@/context/MeContext';
import { useJobsDashboardPolling } from '@/hooks/useJobsDashboardPolling';
import { useJobsCleanupEligibility } from '@/hooks/useJobsCleanupEligibility';
import { useJobsListPolling } from '@/hooks/useJobsListPolling';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import {
  canCancelJob,
  canRemoveJob,
  canRetryJob,
  formatJobType,
  isTerminalJobStatus,
  jobStatusLabel,
  jobStatusVariant,
  shortId,
} from '@/utils/jobStatus';
import {
  describeJobsDashboardFilters,
  jobsDashboardHasFilters,
} from '@/utils/jobsDashboardFilters';
import { jobsCleanupRefreshSignal } from '@/utils/jobsCleanupRefreshSignal';
import { useWorkspaceScopedEffect } from '@/hooks/useWorkspaceScopedEffect';

const PAGE_SIZE = 50;
const PANEL_SCOPE = 'jobs-page';
const JOBS_WORKSPACE_SCOPE_KEY = 'jobs-workspace-scope';

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'scrape_scheduled', label: 'Scheduled scrape' },
  { value: 'scrape_search', label: 'Manual search scrape' },
  { value: 'scrape_subscription', label: 'Subscription scrape' },
  { value: 'send_batch', label: 'Notification send batch' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'partial_failed', label: 'Partially failed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function formatTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export default function JobsDashboardPage() {
  const { toast } = useToast();
  const { me } = useMe();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManageJobs = me?.permissions.canManageJobs ?? false;
  const canUseAllWorkspacesView =
    canManageJobs && Boolean(me?.permissions.canSwitchWorkspace);
  const [jobsWorkspaceScope, setJobsWorkspaceScope] = useState<'scoped' | 'all'>(() => {
    if (typeof window === 'undefined') return 'scoped';
    const stored = window.sessionStorage.getItem(JOBS_WORKSPACE_SCOPE_KEY);
    return stored === 'all' ? 'all' : 'scoped';
  });
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchIdFilter, setSearchIdFilter] = useState('');
  const [subscriptionIdFilter, setSubscriptionIdFilter] = useState('');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [rowActionId, setRowActionId] = useState<string | null>(null);
  const [removeJob, setRemoveJob] = useState<Job | null>(null);
  const [cleanupMode, setCleanupMode] = useState<JobCleanupMode | null>(null);
  const [clearActivityJobId, setClearActivityJobId] = useState<string | null>(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteAlsoClearActivity, setBulkDeleteAlsoClearActivity] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulk = useBulkSelection<Job>();

  const clearSelectedJob = useCallback(() => {
    setSelectedJobId(null);
    if (searchParams.has('job_id')) {
      const next = new URLSearchParams(searchParams);
      next.delete('job_id');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useWorkspaceScopedEffect(() => {
    clearSelectedJob();
  }, [clearSelectedJob]);

  useEffect(() => {
    const jobId = searchParams.get('job_id');
    if (jobId) setSelectedJobId(jobId);
  }, [searchParams]);

  useEffect(() => {
    if (!canUseAllWorkspacesView && jobsWorkspaceScope === 'all') {
      setJobsWorkspaceScope('scoped');
    }
  }, [canUseAllWorkspacesView, jobsWorkspaceScope]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(JOBS_WORKSPACE_SCOPE_KEY, jobsWorkspaceScope);
  }, [jobsWorkspaceScope]);

  const listParams = useMemo<JobListParams>(
    () => ({
      type: typeFilter || undefined,
      status: statusFilter || undefined,
      search_id: searchIdFilter || undefined,
      subscription_id: subscriptionIdFilter || undefined,
      workspace: canUseAllWorkspacesView && jobsWorkspaceScope === 'all' ? 'all' : undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [
      page,
      typeFilter,
      statusFilter,
      searchIdFilter,
      subscriptionIdFilter,
      canUseAllWorkspacesView,
      jobsWorkspaceScope,
    ]
  );

  const {
    jobs,
    summary,
    total,
    loading,
    error,
    refresh: refreshDashboard,
    autoRefresh,
  } = useJobsListPolling({ params: listParams });

  const cleanupEligibility = useJobsCleanupEligibility(
    canManageJobs,
    jobsCleanupRefreshSignal(summary)
  );

  const {
    openJob,
    openJobError,
    openJobLoading,
    refreshOpenJob,
    setItemStatusFilter,
    setAttemptStatusFilter,
    restartJobPolling,
  } = useJobsDashboardPolling(selectedJobId);

  useEffect(() => {
    if (!selectedJobId || openJobLoading || !openJobError) return;
    const notFound = openJobError.toLowerCase().includes('not found');
    if (notFound) {
      clearSelectedJob();
    }
  }, [clearSelectedJob, openJobError, openJobLoading, selectedJobId]);

  const activeWorkers = useMemo(() => {
    if (!summary?.workers) return 0;
    return summary.workers.filter((w) => !w.stale && w.status !== 'stale').length;
  }, [summary]);

  const hasNextPage = page * PAGE_SIZE < total;
  const paginationPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : hasNextPage ? page + 1 : page;
  const paginationTotal = total > 0 ? total : (page - 1) * PAGE_SIZE + jobs.length;

  const selectedTerminalJobIds = useMemo(
    () =>
      Array.from(bulk.selectedKeys).filter((id) => {
        const job = jobs.find((row) => row.id === id);
        return job ? canRemoveJob(job.status) : false;
      }),
    [bulk.selectedKeys, jobs],
  );

  const handleRowAction = useCallback(
    async (job: Job, action: 'cancel' | 'retry' | 'refresh', event?: React.MouseEvent) => {
      event?.stopPropagation();
      setRowActionId(job.id);
      try {
        if (action === 'cancel') {
          await api.jobs.cancel(job.id);
          toast('Job cancelled', 'info');
        } else if (action === 'retry') {
          await api.jobs.retry(job.id);
          toast('Job requeued for retry', 'success');
        }
        await refreshDashboard();
        if (selectedJobId === job.id) {
          await refreshOpenJob();
        }
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Action failed', 'error');
      } finally {
        setRowActionId(null);
      }
    },
    [refreshDashboard, refreshOpenJob, selectedJobId, toast]
  );

  async function handleDrawerActionComplete() {
    const result = await refreshOpenJob();
    await refreshDashboard();
    if (result?.job && !isTerminalJobStatus(result.job.status)) {
      restartJobPolling();
    }
  }

  const runBulk = useCallback(
    async (
      label: string,
      action: (ids: string[]) => Promise<{ failed?: number; deleted?: number; cancelled?: number; retried?: number }>,
    ) => {
      const ids = Array.from(bulk.selectedKeys);
      if (!ids.length) return;
      setBulkBusy(true);
      try {
        const result = await action(ids);
        const ok =
          (result.deleted ?? 0) + (result.cancelled ?? 0) + (result.retried ?? 0);
        const failed = result.failed ?? 0;
        if (failed > 0) {
          toast(`${label}: ${ok} succeeded, ${failed} failed`, 'info');
        } else {
          toast(`${label}: ${ok} job(s)`, 'success');
        }
        bulk.clearSelection();
        await refreshDashboard();
      } catch (e) {
        toast(e instanceof Error ? e.message : `${label} failed`, 'error');
      } finally {
        setBulkBusy(false);
      }
    },
    [bulk, refreshDashboard, toast],
  );

  const jobPageKeys = useMemo(() => jobs.map((j) => j.id), [jobs]);

  const completedCleanupDisabled =
    cleanupEligibility.loading || cleanupEligibility.completedCount === 0;
  const failedCleanupDisabled = cleanupEligibility.loading || cleanupEligibility.failedCount === 0;

  const columns = useMemo<DataTableColumn<Job>[]>(() => {
    const base: DataTableColumn<Job>[] = [
      {
        id: 'created_at',
        header: 'Created',
        width: 150,
        sortable: true,
        sortValue: (job) => job.created_at,
        render: (job) => formatTime(job.created_at),
      },
    ];
    if (canUseAllWorkspacesView && jobsWorkspaceScope === 'all') {
      base.push({
        id: 'workspace',
        header: 'Workspace',
        width: 160,
        sortable: true,
        sortValue: (job) => job.workspace_label ?? '',
        render: (job) => job.workspace_label ?? '—',
      });
    }
    base.push(
      {
        id: 'type',
        header: 'Type',
        width: 160,
        sortable: true,
        sortValue: (job) => job.type,
        render: (job) => formatJobType(job.type),
      },
      {
        id: 'status',
        header: 'Status',
        width: 120,
        sortable: true,
        sortValue: (job) => job.status,
        render: (job) => (
          <StatusBadge variant={jobStatusVariant(job.status)}>{jobStatusLabel(job.status)}</StatusBadge>
        ),
      },
      {
        id: 'progress',
        header: 'Progress',
        width: 120,
        render: (job) => {
          const pct = job.total_items > 0 ? Math.round(job.progress_percent) : 0;
          return (
            <div className="jobs-table-progress">
              <div className="job-progress-bar" aria-hidden>
                <div className="job-progress-bar__fill" style={{ width: `${pct}%` }} />
              </div>
              <span className="muted">{pct}%</span>
            </div>
          );
        },
      },
      {
        id: 'total_items',
        header: 'Total',
        width: 70,
        sortable: true,
        sortValue: (job) => job.total_items,
        render: (job) => job.total_items,
      },
      {
        id: 'processed_items',
        header: 'Processed',
        width: 90,
        sortable: true,
        sortValue: (job) => job.processed_items,
        render: (job) => job.processed_items,
      },
      {
        id: 'failed_items',
        header: 'Failed',
        width: 70,
        sortable: true,
        sortValue: (job) => job.failed_items,
        render: (job) => job.failed_items,
      },
      {
        id: 'search_id',
        header: 'Search',
        width: 100,
        render: (job) =>
          job.search_id ? (
            <span className="mono-id" title={job.search_id}>
              {shortId(job.search_id)}
            </span>
          ) : (
            '—'
          ),
      },
      {
        id: 'subscription_id',
        header: 'Subscription',
        width: 110,
        render: (job) =>
          job.subscription_id ? (
            <span className="mono-id" title={job.subscription_id}>
              {shortId(job.subscription_id)}
            </span>
          ) : (
            '—'
          ),
      },
      {
        id: 'started_at',
        header: 'Started',
        width: 150,
        sortable: true,
        sortValue: (job) => job.started_at,
        render: (job) => formatTime(job.started_at),
      },
      {
        id: 'finished_at',
        header: 'Finished',
        width: 150,
        sortable: true,
        sortValue: (job) => job.finished_at,
        render: (job) => formatTime(job.finished_at),
      },
      {
        id: 'actions',
        header: 'Actions',
        width: TABLE_ACTION_MIN_WIDTH.jobs,
        minWidth: TABLE_ACTION_MIN_WIDTH.jobs,
        actions: true,
        resizable: false,
        className: tableActionsColumnClass('jobs'),
        render: (job) => (
          <TableActionBar
            danger={
              canManageJobs && canRemoveJob(job.status) ? (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  disabled={rowActionId === job.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setRemoveJob(job);
                  }}
                >
                  Remove
                </button>
              ) : undefined
            }
          >
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={rowActionId === job.id}
              onClick={(e) => void handleRowAction(job, 'refresh', e)}
            >
              Refresh
            </button>
            {canRetryJob(job.status) && (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={rowActionId === job.id}
                onClick={(e) => void handleRowAction(job, 'retry', e)}
              >
                Retry
              </button>
            )}
            {canCancelJob(job.status) && (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={rowActionId === job.id}
                onClick={(e) => void handleRowAction(job, 'cancel', e)}
              >
                Cancel
              </button>
            )}
            {canManageJobs && (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={rowActionId === job.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setClearActivityJobId(job.id);
                }}
              >
                Clear activity
              </button>
            )}
          </TableActionBar>
        ),
      },
    );
    return base;
  }, [canManageJobs, canUseAllWorkspacesView, jobsWorkspaceScope, handleRowAction, rowActionId]);

  const hasFilters = jobsDashboardHasFilters({
    typeFilter,
    statusFilter,
    searchIdFilter,
    subscriptionIdFilter,
  });
  const activeFilterLabels = useMemo(
    () =>
      describeJobsDashboardFilters({
        typeFilter,
        statusFilter,
        searchIdFilter,
        subscriptionIdFilter,
      }),
    [typeFilter, statusFilter, searchIdFilter, subscriptionIdFilter]
  );

  const clearFilters = useCallback(() => {
    setPage(1);
    setTypeFilter('');
    setStatusFilter('');
    setSearchIdFilter('');
    setSubscriptionIdFilter('');
  }, []);

  return (
    <div className="page jobs-dashboard-page">
      <PageHeader
        title="Jobs"
        subtitle="Operational view of durable scrape and send pipelines — queue depth, workers, and per-job drill-down."
      >
        <div className="jobs-dashboard-header-actions">
          <AutoRefreshToolbar control={autoRefresh} intervalLabel="Adaptive (4–8s)" />
        </div>
      </PageHeader>

      {error && <ErrorBanner message={error} />}

      <AdminPageSections>
        <CollapsiblePanel
          id="summary"
          storageScope={PANEL_SCOPE}
          title="Queue summary"
          defaultExpanded
        >
      <div className="metric-chips jobs-summary-row">
        <MetricChip label="Queued jobs" value={summary?.jobs.queued ?? '—'} variant="info" />
        <MetricChip label="Processing jobs" value={summary?.jobs.processing ?? '—'} variant="info" />
        <MetricChip label="Failed jobs" value={summary?.jobs.failed ?? '—'} variant="danger" />
        <MetricChip
          label="Dead-letter items"
          value={summary?.items.dead_letter ?? '—'}
          variant="danger"
        />
        <MetricChip label="Scrape wake queue" value={summary?.queues.scrape_wake_depth ?? '—'} />
        <MetricChip label="Send wake queue" value={summary?.queues.send_wake_depth ?? '—'} />
        <MetricChip label="Active workers" value={activeWorkers} variant="success" />
      </div>
        </CollapsiblePanel>

        <CollapsiblePanel
          id="job-list"
          storageScope={PANEL_SCOPE}
          title="Jobs"
          badge={total > 0 ? `${jobs.length} / ${total}` : jobs.length}
          defaultExpanded
          unmountWhenCollapsed
        >
        <Toolbar>
          <FilterSelect label="Type" htmlFor="jobs-type-filter">
            <select
              id="jobs-type-filter"
              className="filter-select"
              value={typeFilter}
              onChange={(e) => {
                setPage(1);
                setTypeFilter(e.target.value);
              }}
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FilterSelect>
          <FilterSelect label="Status" htmlFor="jobs-status-filter">
            <select
              id="jobs-status-filter"
              className="filter-select"
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value);
              }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FilterSelect>
          {canUseAllWorkspacesView ? (
            <FilterSelect label="Workspace" htmlFor="jobs-workspace-filter">
              <select
                id="jobs-workspace-filter"
                className="filter-select"
                value={jobsWorkspaceScope}
                onChange={(e) => {
                  setPage(1);
                  setJobsWorkspaceScope(e.target.value === 'all' ? 'all' : 'scoped');
                }}
              >
                <option value="scoped">Current workspace</option>
                <option value="all">All workspaces</option>
              </select>
            </FilterSelect>
          ) : null}
          <FilterSelect label="Search ID" htmlFor="jobs-search-filter">
            <input
              id="jobs-search-filter"
              className="filter-select"
              type="text"
              placeholder="UUID"
              value={searchIdFilter}
              onChange={(e) => {
                setPage(1);
                setSearchIdFilter(e.target.value.trim());
              }}
            />
          </FilterSelect>
          <FilterSelect label="Subscription ID" htmlFor="jobs-subscription-filter">
            <input
              id="jobs-subscription-filter"
              className="filter-select"
              type="text"
              placeholder="UUID"
              value={subscriptionIdFilter}
              onChange={(e) => {
                setPage(1);
                setSubscriptionIdFilter(e.target.value.trim());
              }}
            />
          </FilterSelect>
          {hasFilters && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </Toolbar>

        {loading && jobs.length === 0 ? (
          <div className="loading-inline" role="status">
            <span className="spinner" aria-hidden />
            Loading jobs…
          </div>
        ) : (
          <>
            {!canManageJobs ? (
              <p className="muted jobs-permission-hint">
                Job management actions require owner or admin permissions.
              </p>
            ) : null}
            {canManageJobs ? (
              <BulkActionBar
                selectedCount={bulk.selectedCount}
                totalFiltered={jobs.length}
                onClear={bulk.clearSelection}
                onSelectPage={() => bulk.togglePage(jobPageKeys)}
                onSelectFiltered={() => bulk.selectFiltered(jobPageKeys)}
                loading={bulkBusy}
              >
                <BulkActionButton
                  loading={bulkBusy}
                  disabled={bulk.selectedCount === 0}
                  onClick={() => runBulk('Retry', (ids) => api.jobs.bulkRetry(ids))}
                >
                  Retry selected
                </BulkActionButton>
                <BulkActionButton
                  loading={bulkBusy}
                  disabled={bulk.selectedCount === 0}
                  onClick={() => runBulk('Cancel', (ids) => api.jobs.bulkCancel(ids))}
                >
                  Cancel selected
                </BulkActionButton>
                <BulkActionButton
                  loading={bulkBusy}
                  variant="danger"
                  disabled={selectedTerminalJobIds.length === 0}
                  onClick={() => setCleanupMode('selected')}
                >
                  Clear selected ({selectedTerminalJobIds.length})
                </BulkActionButton>
                <BulkActionButton
                  loading={bulkBusy}
                  variant="danger"
                  disabled={bulk.selectedCount === 0}
                  onClick={() => setShowBulkDeleteModal(true)}
                >
                  Delete selected
                </BulkActionButton>
                <BulkActionButton
                  loading={bulkBusy || cleanupEligibility.loading}
                  variant="danger"
                  disabled={completedCleanupDisabled}
                  title={
                    completedCleanupDisabled && !cleanupEligibility.loading
                      ? 'No completed jobs eligible for cleanup'
                      : undefined
                  }
                  onClick={() => {
                    if (completedCleanupDisabled) return;
                    setCleanupMode('completed');
                  }}
                >
                  Clear completed
                </BulkActionButton>
                <BulkActionButton
                  loading={bulkBusy || cleanupEligibility.loading}
                  variant="danger"
                  disabled={failedCleanupDisabled}
                  title={
                    failedCleanupDisabled && !cleanupEligibility.loading
                      ? 'No failed jobs eligible for cleanup'
                      : undefined
                  }
                  onClick={() => {
                    if (failedCleanupDisabled) return;
                    setCleanupMode('failed');
                  }}
                >
                  Clear failed
                </BulkActionButton>
              </BulkActionBar>
            ) : null}
          <DataTable
            columns={columns}
            rows={jobs}
            getRowKey={(job) => job.id}
            columnStorageKey="jobs-dashboard"
            defaultSort={{ columnId: 'created_at', direction: 'desc' }}
            clientSort={false}
            onRowClick={(job) => setSelectedJobId(job.id)}
            selection={
              canManageJobs
                ? {
                    isSelected: bulk.isSelected,
                    onToggleRow: bulk.toggleRow,
                    pageKeys: jobPageKeys,
                    pageAllSelected: bulk.pageAllSelected(jobPageKeys),
                    pageSomeSelected: bulk.pageSomeSelected(jobPageKeys),
                    onTogglePage: () => bulk.togglePage(jobPageKeys),
                  }
                : undefined
            }
            emptyState={
              <EmptyState
                title={hasFilters ? 'No jobs match current filters' : 'No jobs yet'}
                description={
                  hasFilters
                    ? `Active filters: ${activeFilterLabels.join(' · ')}`
                    : 'Durable scrape and send jobs appear here once workers process work.'
                }
                action={
                  hasFilters ? (
                    <button type="button" className="btn btn-sm" onClick={clearFilters}>
                      Clear filters
                    </button>
                  ) : undefined
                }
              />
            }
            toolbar={{
              onRefresh: () => void refreshDashboard(),
              rightSlot: (
                <ExportButtons
                  path="/exports/jobs"
                  params={{
                    type: typeFilter || undefined,
                    status: statusFilter || undefined,
                    search_id: searchIdFilter || undefined,
                    subscription_id: subscriptionIdFilter || undefined,
                  }}
                />
              ),
            }}
          />
          </>
        )}

        <Pagination
          page={page}
          pages={paginationPages}
          total={paginationTotal}
          onPage={setPage}
        />
        </CollapsiblePanel>

      {summary?.workers && summary.workers.length > 0 && (
        <CollapsiblePanel
          id="workers"
          storageScope={PANEL_SCOPE}
          title="Workers"
          badge={summary.workers.length}
          defaultExpanded={false}
        >
          <div className="table-scroll data-table-mobile-cards--table-only">
            <table className="data-table admin-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Worker</th>
                  <th>Status</th>
                  <th>Current job</th>
                  <th>Last heartbeat</th>
                  <th>Processed</th>
                  <th>Failed</th>
                </tr>
              </thead>
              <tbody>
                {summary.workers.map((worker) => (
                  <tr key={worker.worker_id}>
                    <td>{worker.role}</td>
                    <td className="mono-id">
                      <TruncatedCell value={worker.worker_id} maxWidth={160} sensitive />
                    </td>
                    <td>
                      <StatusBadge variant={worker.stale ? 'warning' : jobStatusVariant(worker.status)}>
                        {worker.stale ? 'Stale' : worker.status}
                      </StatusBadge>
                    </td>
                    <td>
                      {worker.current_job ? (
                        <TruncatedCell value={worker.current_job} maxWidth={200} sensitive />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{formatTime(worker.last_heartbeat_at)}</td>
                    <td>{worker.processed_count}</td>
                    <td>{worker.failed_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsiblePanel>
      )}

      </AdminPageSections>

      {selectedJobId && (
        <JobDetailDrawer
          jobId={selectedJobId}
          job={openJob.job}
          items={openJob.items}
          attempts={openJob.attempts}
          loading={openJobLoading}
          error={openJobError}
          onClose={clearSelectedJob}
          onRefresh={refreshOpenJob}
          onActionComplete={handleDrawerActionComplete}
          onItemStatusFilterChange={setItemStatusFilter}
          onAttemptStatusFilterChange={setAttemptStatusFilter}
          canManageJobs={canManageJobs}
          onClearActivity={canManageJobs ? setClearActivityJobId : undefined}
        />
      )}

      {removeJob && (
        <ConfirmRemoveJobModal
          job={removeJob}
          onClose={() => setRemoveJob(null)}
          onDone={async (message) => {
            toast(message, 'success');
            if (selectedJobId === removeJob.id) {
              setSelectedJobId(null);
            }
            await refreshDashboard();
          }}
        />
      )}

      {cleanupMode && (
        <CleanupJobsConfirmModal
          mode={cleanupMode}
          jobIds={cleanupMode === 'selected' ? selectedTerminalJobIds : undefined}
          onClose={() => setCleanupMode(null)}
          onDone={async (message) => {
            toast(message, message.includes('No ') ? 'info' : 'success');
            bulk.clearSelection();
            await refreshDashboard();
            await cleanupEligibility.refresh();
          }}
        />
      )}

      {clearActivityJobId && (
        <ClearJobActivityConfirmModal
          jobId={clearActivityJobId}
          onClose={() => setClearActivityJobId(null)}
          onDone={async (message) => {
            toast(message, 'success');
            if (selectedJobId === clearActivityJobId) {
              setSelectedJobId(null);
            }
            setClearActivityJobId(null);
            await refreshDashboard();
          }}
        />
      )}

      {showBulkDeleteModal && (
        <BulkDeleteJobsConfirmModal
          selectedCount={bulk.selectedCount}
          alsoClearActivity={bulkDeleteAlsoClearActivity}
          onAlsoClearActivityChange={setBulkDeleteAlsoClearActivity}
          onClose={() => setShowBulkDeleteModal(false)}
          onConfirm={async () => {
            const ids = Array.from(bulk.selectedKeys);
            setBulkBusy(true);
            try {
              const result = await api.jobs.bulkDelete(ids, {
                alsoClearActivity: bulkDeleteAlsoClearActivity,
              });
              const deleted = result.deleted ?? 0;
              const activity = result.activity_deleted ?? 0;
              const failed = result.failed ?? 0;
              if (failed > 0) {
                toast(`Removed ${deleted} job(s); ${failed} could not be removed`, 'info');
              } else if (activity > 0) {
                toast(`Removed ${deleted} job(s) and ${activity} activity record(s)`, 'success');
              } else {
                toast(`Removed ${deleted} job(s)`, 'success');
              }
              bulk.clearSelection();
              await refreshDashboard();
            } finally {
              setBulkBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}
